import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { osFetch } from "../api";
import { useOS } from "../OSContext";
import { DemoResetButton } from "./DemoResetButton";
import { toast } from "sonner";
import {
  Loader2, Sparkles, Radar, Newspaper, Boxes, FolderKanban, Scale, Flag, Map, Gauge,
  TrendingUp, Rocket, GitBranch, ArrowRight, Target, Compass, CalendarClock, Zap, ShieldAlert, Lightbulb, RefreshCw,
} from "lucide-react";

const HEALTH_HEX = (v) => (v >= 70 ? "#16C784" : v >= 45 ? "#F0B90B" : "#F6465D");
const DOT = { green: "bg-emerald-500", yellow: "bg-amber-500", red: "bg-rose-500", slate: "bg-slate-600", unknown: "bg-slate-600" };
const SNAP_META = {
  company_health: { label: "Company Health", icon: Gauge }, confidence: { label: "Confidence", icon: TrendingUp },
  ai_confidence: { label: "AI Confidence", icon: Sparkles }, active_products: { label: "Active Products", icon: Boxes },
  active_projects: { label: "Active Projects", icon: FolderKanban }, open_decisions: { label: "Open Decisions", icon: Scale },
  open_attention: { label: "Open Attention", icon: Radar }, active_milestones: { label: "Active Milestones", icon: Flag },
  blockers: { label: "Blockers", icon: ShieldAlert }, ai_recommendations: { label: "AI Recommendations", icon: Lightbulb },
};

function HealthRing({ value, size = 92 }) {
  const r = (size - 12) / 2, c = 2 * Math.PI * r, off = c - (value / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="dash-health-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={HEALTH_HEX(value)} strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display font-extrabold text-2xl" style={{ color: HEALTH_HEX(value) }}>{value}</span>
        <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500">health</span>
      </div>
    </div>
  );
}

const Panel = ({ title, icon: Icon, right, children, testid, className = "" }) => (
  <section className={`os-glass rounded-3xl p-6 os-fade ${className}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon size={16} className="text-[#00F0FF]" />}
      <h2 className="font-display font-bold text-lg">{title}</h2>
      {right && <div className="ml-auto">{right}</div>}
    </div>
    {children}
  </section>
);

export default function ExecutiveDashboard() {
  const { ctx } = useOS();
  const [d, setD] = useState(null);
  const [recs, setRecs] = useState(null);
  const [genRecs, setGenRecs] = useState(false);
  const [now, setNow] = useState(new Date());

  const load = useCallback(() => osFetch("/dashboard").then(setD).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); osFetch("/dashboard/recommendations/latest").then(setRecs).catch(() => {}); }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);

  const runRecs = async () => {
    setGenRecs(true); toast.info("ArcaOS is prioritizing your day…");
    try { setRecs(await osFetch("/dashboard/recommendations", { method: "POST" })); toast.success("Recommendations ready"); }
    catch (e) { toast.error(e.message); } finally { setGenRecs(false); }
  };

  if (!d) return <div className="flex items-center gap-2 text-slate-400 py-24"><Loader2 className="animate-spin" /> Loading Mission Control…</div>;
  const h = d.header;

  return (
    <div className="max-w-7xl mx-auto" data-testid="os-executive-dashboard">
      {/* HEADER */}
      <div className="os-glass rounded-3xl p-6 os-fade">
        <div className="flex items-start gap-6 flex-wrap">
          <HealthRing value={h.company_health} />
          <div className="flex-1 min-w-[260px]">
            <p className="font-mono text-[11px] uppercase tracking-widest text-[#00F0FF]">Mission Control</p>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl" data-testid="dash-company-name">{h.company_name}</h1>
            <div className="flex items-start gap-2 mt-3 text-slate-300 max-w-2xl"><Compass size={15} className="text-[#00F0FF] mt-0.5 shrink-0" /><p className="text-sm" data-testid="dash-north-star">{h.north_star}</p></div>
          </div>
          <div className="text-right">
            <p className="font-mono text-xs text-slate-400" data-testid="dash-datetime">{now.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</p>
            <p className="font-display font-bold text-2xl">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
            <Link to="/os/brief" className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline" data-testid="dash-brief-link"><Newspaper size={12} /> CEO Daily Brief</Link>
            <div><DemoResetButton /></div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-5">
          <Tag icon={Target} label="Phase" value={h.current_phase} />
          <Tag icon={Zap} label="Sprint" value={h.current_sprint} />
        </div>
      </div>

      {/* SNAPSHOT */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-5">
        {Object.entries(d.snapshot).map(([k, v]) => {
          const m = SNAP_META[k] || { label: k, icon: Gauge };
          return (
            <Link key={k} to={v.drill} data-testid={`dash-snap-${k}`} className="os-glass rounded-2xl p-4 hover:border-[#0055FF]/40 hover:-translate-y-0.5 transition-all duration-300 group">
              <div className="flex items-center gap-2 text-slate-500"><m.icon size={14} className="text-[#00F0FF]" /><span className="text-[10px] font-mono uppercase tracking-widest">{m.label}</span></div>
              <p className="font-display font-extrabold text-3xl mt-2">{v.value ?? "—"}<span className="text-base text-slate-600">{v.suffix || ""}</span></p>
            </Link>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-5">
        {/* AI RECOMMENDATION CENTER */}
        <Panel title="What should I work on today?" icon={Sparkles} testid="dash-recommendations" className="lg:col-span-2"
          right={<button onClick={runRecs} disabled={genRecs} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline" data-testid="dash-recs-generate">{genRecs ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} {recs ? "Refresh" : "Generate"}</button>}>
          {!recs ? <p className="text-slate-600 text-sm">Generate to get today&apos;s highest-ROI, highest-risk and biggest-opportunity focus — always with the reasoning.</p> : (
            <>
              <div className="rounded-2xl border border-[#0055FF]/30 bg-[#0055FF]/10 p-4 mb-4">
                <p className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-1">Focus today</p>
                <p className="text-white text-[15px]">{recs.work_on_today}</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {(recs.lenses || []).map((l, i) => (
                  <div key={i} className="rounded-xl border border-white/10 bg-black/30 p-3" data-testid={`dash-lens-${i}`}>
                    <div className="flex items-center justify-between"><span className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">{l.lens}</span><span className="text-[10px] font-mono text-slate-600">{l.confidence}%</span></div>
                    <p className="text-sm text-white mt-1.5">{l.recommendation}</p>
                    <p className="text-xs text-slate-500 mt-1"><span className="text-slate-600">Why:</span> {l.why}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </Panel>

        {/* ATTENTION CENTER */}
        <Panel title="AI Attention Center" icon={Radar} testid="dash-attention"
          right={<Link to="/os/attention" className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline">Open →</Link>}>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {["Critical", "High", "Medium", "Low"].map((s) => (
              <div key={s} className="rounded-xl border border-white/10 bg-black/30 p-2.5 text-center" data-testid={`dash-attn-${s.toLowerCase()}`}>
                <p className={`font-display font-extrabold text-xl ${s === "Critical" ? "text-rose-300" : s === "High" ? "text-orange-300" : s === "Medium" ? "text-amber-300" : "text-slate-300"}`}>{d.attention.by_severity[s]}</p>
                <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500">{s}</p>
              </div>
            ))}
          </div>
          <ul className="space-y-2">
            {d.attention.top_groups.slice(0, 4).map((g) => (
              <li key={g.id}><Link to="/os/attention" className="flex items-start gap-2 group"><span className={`shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-display font-bold ${g.priority_score >= 70 ? "bg-rose-500/15 text-rose-300" : g.priority_score >= 40 ? "bg-amber-500/15 text-amber-300" : "bg-white/5 text-slate-300"}`}>{g.priority_score}</span><span className="text-xs text-slate-300 group-hover:text-white leading-snug">{g.headline}</span></Link></li>
            ))}
            {d.attention.top_groups.length === 0 && <p className="text-slate-600 text-sm">All clear.</p>}
          </ul>
        </Panel>
      </div>

      {/* PRODUCT HEALTH */}
      <Panel title="Product Health" icon={Boxes} testid="dash-products" className="mt-5"
        right={<Link to="/os/catalog" className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline">Catalog →</Link>}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {d.products.map((p) => (
            <Link key={p.id} to={`/os/catalog/${p.id}`} data-testid={`dash-product-${p.id}`} className="rounded-2xl border border-white/10 bg-black/30 p-4 hover:border-[#0055FF]/40 transition-colors">
              <div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${DOT[p.health] || DOT.unknown}`} /><p className="font-display font-bold">{p.name}</p><span className="ml-auto text-[9px] font-mono uppercase tracking-widest text-slate-500">{p.lifecycle}</span></div>
              <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-[#00F0FF]" style={{ width: `${p.progress}%` }} /></div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2.5 text-[11px] font-mono text-slate-500">
                <span>progress {p.progress}%</span><span>· {p.milestones} ms</span><span>· {p.attention} attn</span>{p.confidence != null && <span>· conf {p.confidence}</span>}<span>· {p.recommendations} recs</span>
              </div>
            </Link>
          ))}
        </div>
        {/* Reserved future products */}
        <div className="flex flex-wrap gap-2 mt-4" data-testid="dash-future-products">
          {["ArcaCommerce", "Workforce Platform", "Vacation Homes"].map((f) => (
            <span key={f} className="text-[10px] font-mono uppercase tracking-widest text-slate-600 border border-dashed border-white/10 rounded-full px-3 py-1.5">{f} · reserved</span>
          ))}
        </div>
      </Panel>

      {/* ROADMAP + RELEASE */}
      <div className="grid lg:grid-cols-3 gap-5 mt-5">
        <Panel title="Roadmap" icon={Map} testid="dash-roadmap" className="lg:col-span-2"
          right={<Link to="/os/roadmap" className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline">Roadmap →</Link>}>
          <div className="grid sm:grid-cols-3 gap-3">
            {["now", "next", "later"].map((hz) => (
              <div key={hz} data-testid={`dash-roadmap-${hz}`}>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">{hz}</p>
                <div className="space-y-2">
                  {d.roadmap[hz].length === 0 && <p className="text-slate-700 text-xs">—</p>}
                  {d.roadmap[hz].map((r) => (
                    <Link key={r.id} to={`/os/roadmap/${r.id}`} className="block rounded-xl border border-white/10 bg-black/30 p-3 hover:border-[#0055FF]/40 transition-colors">
                      <div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${DOT[r.health]}`} /><p className="text-sm text-slate-200 leading-snug">{r.title}</p></div>
                      <div className="mt-2 h-1 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full bg-[#00F0FF]" style={{ width: `${r.completion}%` }} /></div>
                      <div className="flex gap-2 mt-1.5 text-[10px] font-mono text-slate-600"><span>{r.completion}%</span>{r.dependencies > 0 && <span>· {r.dependencies} deps</span>}{r.attention > 0 && <span>· {r.attention} attn</span>}</div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Release Center" icon={Rocket} testid="dash-release">
          <div className="flex items-center gap-3">
            <span className={`w-3 h-3 rounded-full ${DOT[d.release.health]}`} />
            <p className="font-display font-bold text-lg">{d.release.current_release || "—"}</p>
          </div>
          {d.release.countdown_days != null && <p className="text-[13px] text-slate-400 mt-1"><CalendarClock size={13} className="inline mr-1 text-[#00F0FF]" />{d.release.countdown_days >= 0 ? `${d.release.countdown_days} days to release` : `${-d.release.countdown_days} days overdue`}</p>}
          <div className="mt-4"><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Deployment readiness</p><div className="h-2 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${d.release.deployment_readiness}%`, background: HEALTH_HEX(d.release.deployment_readiness) }} /></div><p className="text-right text-[11px] font-mono text-slate-500 mt-1">{d.release.deployment_readiness}%</p></div>
          <ul className="mt-3 space-y-1.5 text-sm text-slate-400">
            <li className="flex justify-between"><span>Blocking decisions</span><span className="text-white">{d.release.blocking_decisions}</span></li>
            <li className="flex justify-between"><span>Open bugs</span><span className="text-slate-600 text-xs">not tracked yet</span></li>
            <li className="flex justify-between"><span>Missing docs</span><span className="text-slate-600 text-xs">not tracked yet</span></li>
          </ul>
        </Panel>
      </div>

      {/* PIPELINE + GRAPH */}
      <div className="grid lg:grid-cols-2 gap-5 mt-5">
        <Panel title="Discovery Pipeline" icon={Sparkles} testid="dash-pipeline"
          right={<Link to="/studio" className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline">New idea →</Link>}>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <Stat label="Ideas" value={d.pipeline.ideas} /><Stat label="Ready to sync" value={d.pipeline.ready_to_sync} /><Stat label="Synced" value={d.pipeline.already_synced} />
          </div>
          <ul className="space-y-2">
            {d.pipeline.newest.length === 0 && <p className="text-slate-600 text-sm">No discovery sessions yet.</p>}
            {d.pipeline.newest.map((s) => (
              <li key={s.session_id} className="rounded-xl border border-white/10 bg-black/30 p-3 flex items-center gap-3" data-testid={`dash-pipeline-${s.session_id}`}>
                <div className="min-w-0 flex-1"><p className="text-sm text-slate-200 truncate">{s.app_name || s.idea}</p><p className="text-[10px] font-mono text-slate-600 uppercase">{s.status}{s.opportunity_score != null ? ` · opp ${s.opportunity_score}` : ""}{s.synced ? " · synced" : ""}</p></div>
                {s.status === "ready" && !s.synced && <SyncButton sessionId={s.session_id} onDone={load} />}
                {s.status === "ready" && <Link to={`/studio/discovery/${s.session_id}`} className="text-[#00F0FF]" aria-label="Open discovery session"><ArrowRight size={15} /></Link>}
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Knowledge Graph" icon={GitBranch} testid="dash-graph"><GraphPreview /></Panel>
      </div>

      {/* CEO METRICS */}
      <Panel title="CEO Metrics" icon={TrendingUp} testid="dash-metrics" className="mt-5">
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {[["Products", d.metrics.products], ["Projects", d.metrics.projects], ["Milestones", d.metrics.milestones], ["Completed", d.metrics.completed_work], ["Company Health", d.metrics.company_health], ["Attention", d.metrics.attention_score], ["Knowledge", d.metrics.knowledge_growth], ["Decision Vel.", d.metrics.decision_velocity], ["Release Vel.", d.metrics.release_velocity]].map(([l, v]) => <Stat key={l} label={l} value={v} />)}
        </div>
        <div className="mt-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Recent activity</p>
          <div className="rounded-2xl border border-white/10 bg-black/20 divide-y divide-white/5">
            {d.metrics.recent_activity.length === 0 && <p className="p-4 text-slate-600 text-sm">No activity yet.</p>}
            {d.metrics.recent_activity.slice(0, 8).map((a) => (
              <div key={a.id} className="px-4 py-2.5 text-sm text-slate-300 flex items-center gap-3"><span className="text-[10px] font-mono text-slate-600 w-40 shrink-0">{new Date(a.created_at).toLocaleString()}</span><span className="truncate">{a.summary || `${a.verb} ${a.entity_type}`}</span></div>
            ))}
          </div>
        </div>
      </Panel>

      {/* FUTURE MODULES */}
      <Panel title="Reserved — future modules" icon={Boxes} testid="dash-future" className="mt-5">
        <div className="flex flex-wrap gap-2">
          {d.future_modules.map((f) => <span key={f} className="text-[11px] font-mono text-slate-500 border border-dashed border-white/10 rounded-xl px-3 py-2">{f}</span>)}
        </div>
      </Panel>

      <p className="text-center text-[10px] font-mono text-slate-700 mt-6 mb-2">ArcaOS · {ctx?.org_id} · generated {new Date(h.generated_at).toLocaleTimeString()}</p>
    </div>
  );
}

const Tag = ({ icon: Icon, label, value }) => (
  <div className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 px-3 py-2">
    <Icon size={13} className="text-[#00F0FF]" /><span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</span><span className="text-sm text-slate-200">{value}</span>
  </div>
);
const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-white/10 bg-black/30 p-3 text-center"><p className="font-display font-extrabold text-2xl">{value ?? "—"}</p><p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">{label}</p></div>
);

function SyncButton({ sessionId, onDone }) {
  const [busy, setBusy] = useState(false);
  const sync = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await osFetch(`/bridge/from-discovery/${sessionId}`, { method: "POST" }); toast.success("Synced to ArcaOS"); onDone?.(); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return <button onClick={sync} disabled={busy} className="text-[10px] font-mono uppercase tracking-widest bg-[#0055FF] text-white rounded-lg px-2.5 py-1.5" data-testid={`dash-sync-${sessionId}`}>{busy ? "…" : "Sync"}</button>;
}

function GraphPreview() {
  const [g, setG] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { osFetch("/dashboard/graph?limit=40").then(setG).catch(() => { setErr(true); setG({ nodes: [], edges: [] }); }); }, []);
  if (!g) return <div className="flex items-center gap-2 text-slate-500 text-sm py-8"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
  if (err) return <p className="text-rose-300/80 text-sm py-6">Couldn&apos;t load the graph right now — try again shortly.</p>;
  if (g.nodes.length === 0) return <p className="text-slate-600 text-sm py-6">No relationships yet — sync a discovery or link entities.</p>;
  const W = 460, H = 240, cx = W / 2, cy = H / 2;
  const TYPE_COLOR = { product: "#00F0FF", project: "#0055FF", decision: "#F0B90B", milestone: "#16C784", roadmap_item: "#A78BFA", bible: "#F6465D", business_brief: "#FF7A00" };
  const pos = {}; g.nodes.forEach((n, i) => { const a = (i / g.nodes.length) * 2 * Math.PI; pos[`${n.type}:${n.id}`] = { x: cx + Math.cos(a) * 95, y: cy + Math.sin(a) * 85 }; });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" data-testid="dash-graph-svg">
      {g.edges.map((e, i) => { const a = pos[`${e.from_type}:${e.from_id}`], b = pos[`${e.to_type}:${e.to_id}`]; if (!a || !b) return null; return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="rgba(255,255,255,0.12)" strokeWidth="1" />; })}
      {g.nodes.map((n) => { const p = pos[`${n.type}:${n.id}`]; return <g key={`${n.type}:${n.id}`}><circle cx={p.x} cy={p.y} r="5" fill={TYPE_COLOR[n.type] || "#94a3b8"} /><title>{n.type}: {n.label}</title></g>; })}
    </svg>
  );
}
